import { useId, useState } from 'react';
import Modal from '@/components/ui/Modal/Modal';
import PhaseApplicabilityField from './PhaseApplicabilityField';
import { normalizePhaseApplicability } from '@/services/domain/phaseApplicability';

export default function RulePhaseApplicabilityEditor({ schoolId, domain, rule, onSave }) {
  const formId = useId();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const open = () => {
    setDraft({ value: rule.phaseApplicability ?? { mode: 'whole-school', phaseIds: [] },
      revision: rule.configurationRevision || 0 });
    setError(null);
  };
  const close = () => { if (!busy) { setDraft(null); setError(null); } };
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const phaseApplicability = normalizePhaseApplicability(draft.value);
      await onSave({ phaseApplicability }, rule.id, { expectedRevision: draft.revision });
      setDraft(null);
    } catch (failure) {
      setError({ message: failure.message || 'Rule phases could not be saved.',
        stale: ['aborted', 'functions/aborted'].includes(failure.code) });
    } finally { setBusy(false); }
  };
  return <div className="px-3 pb-2">
    <button type="button" className="btn btn-ghost btn-xs" onClick={open}
      aria-label={`Phase availability for rule ${rule.title || rule.name}`}>
      Applies to: {rule.phaseApplicability?.mode === 'selected-phases' ? 'Selected phases' : 'Whole school'}
    </button>
    <Modal isOpen={Boolean(draft)} nativeDialog size="md" title={`Rule phases: ${rule.title || rule.name}`}
      onClose={close} closeDisabled={busy} actions={<>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={close}>Cancel</button>
        <button type="submit" form={formId} className="btn btn-primary btn-sm" disabled={busy}>Save rule phases</button>
      </>}>
      {draft && <form id={formId} className="space-y-3" onSubmit={save}>
        <PhaseApplicabilityField schoolId={schoolId} domainApplicability={domain.phaseApplicability}
          value={draft.value} disabled={busy} onChange={value => setDraft(current => ({ ...current, value }))} />
        <p className="text-xs text-base-content/65">The resulting action must be available for every phase this rule covers. Phase processes take effect after review and activation.</p>
        {error && <div role="alert" className="text-sm text-error">
          {error.message}
          {error.stale && <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={open}>Load saved rule phases</button>}
        </div>}
      </form>}
    </Modal>
  </div>;
}
